import { useState, useMemo, useCallback } from 'react';
import {
  useGlobalVariable,
  useUpdateGlobalVariable
} from '@api/enterprise/globalVariable';
import { useCertificateTemplates } from '@api/bff/eventPrintTemplates';
import type { CertificateTemplateConfig } from '../types';
import {
  parseCertificateTemplateConfig,
  stringifyCertificateTemplateConfig
} from '../utils';

const SHOW_PRICING_VARIABLE = 'HIDE_PRICING_CUSTOMER_REVIEW';
const CERT_TEMPLATE_VARIABLE = 'HIDE_CERT_TEMPLATE';

const DEFAULT_SHOW_PRICING = true;

export const useGeneralSettingsData = () => {
  const [localShowPricing, setLocalShowPricing] = useState<boolean | null>(
    null
  );
  const [localTemplateType, setLocalTemplateType] = useState<string | null>(
    null
  );
  const [localTemplateName, setLocalTemplateName] = useState<string | null>(
    null
  );

  const {
    data: showPricingData,
    isLoading: isLoadingShowPricing,
    error: showPricingError,
    refetch: refetchShowPricing
  } = useGlobalVariable(SHOW_PRICING_VARIABLE, true);

  const {
    data: certTemplateData,
    isLoading: isLoadingCertTemplate,
    error: certTemplateError,
    refetch: refetchCertTemplate
  } = useGlobalVariable(CERT_TEMPLATE_VARIABLE, true);

  const { data: templateOptions, isLoading: isLoadingTemplateOptions } =
    useCertificateTemplates();

  const updateGlobalVariableMutation = useUpdateGlobalVariable();

  const serverShowPricing = useMemo(() => {
    if (!showPricingData?.text) {
      return DEFAULT_SHOW_PRICING;
    }
    return showPricingData.text === 'true';
  }, [showPricingData?.text]);

  const originalCertConfig = useMemo((): CertificateTemplateConfig => {
    return parseCertificateTemplateConfig(certTemplateData?.blobText);
  }, [certTemplateData?.blobText]);

  const showPricing = localShowPricing ?? serverShowPricing;
  const templateType = localTemplateType ?? originalCertConfig.type;
  const templateName = localTemplateName ?? originalCertConfig.name;

  const hasUnsavedChanges =
    (localShowPricing !== null && localShowPricing !== serverShowPricing) ||
    (localTemplateType !== null &&
      localTemplateType !== originalCertConfig.type) ||
    (localTemplateName !== null &&
      localTemplateName !== originalCertConfig.name);

  const updateShowPricing = useCallback((value: boolean) => {
    setLocalShowPricing(value);
  }, []);

  const updateTemplateType = useCallback((value: string) => {
    setLocalTemplateType(value);
  }, []);

  const updateTemplateName = useCallback((value: string) => {
    setLocalTemplateName(value);
  }, []);

  const saveChanges = useCallback(async () => {
    const savePromises: Promise<unknown>[] = [];

    if (localShowPricing !== null) {
      savePromises.push(
        updateGlobalVariableMutation.mutateAsync({
          name: SHOW_PRICING_VARIABLE,
          command: { text: localShowPricing.toString() }
        })
      );
    }

    if (localTemplateType !== null || localTemplateName !== null) {
      const configToSave: CertificateTemplateConfig = {
        type: localTemplateType ?? originalCertConfig.type,
        name: localTemplateName ?? originalCertConfig.name,
        version: 1
      };

      savePromises.push(
        updateGlobalVariableMutation.mutateAsync({
          name: CERT_TEMPLATE_VARIABLE,
          command: {
            blobText: stringifyCertificateTemplateConfig(configToSave),
            text: certTemplateData?.text || null,
            int: certTemplateData?.int || null,
            date: certTemplateData?.date || null,
            numeric: certTemplateData?.numeric || null
          }
        })
      );
    }

    if (savePromises.length > 0) {
      await Promise.all(savePromises);
    }

    setLocalShowPricing(null);
    setLocalTemplateType(null);
    setLocalTemplateName(null);

    await Promise.all([refetchShowPricing(), refetchCertTemplate()]);
  }, [
    localShowPricing,
    localTemplateType,
    localTemplateName,
    originalCertConfig,
    certTemplateData,
    updateGlobalVariableMutation,
    refetchShowPricing,
    refetchCertTemplate
  ]);

  const discardChanges = useCallback(() => {
    setLocalShowPricing(null);
    setLocalTemplateType(null);
    setLocalTemplateName(null);
  }, []);

  const isLoading = isLoadingShowPricing || isLoadingCertTemplate;

  const displayError = showPricingError || certTemplateError || null;

  const isSaving = updateGlobalVariableMutation.isPending;

  const saveError = updateGlobalVariableMutation.error;

  return {
    showPricing,
    templateType,
    templateName,
    templateOptions: templateOptions ?? [],
    isLoadingTemplates: isLoadingTemplateOptions,
    isLoading,
    error: displayError,
    hasUnsavedChanges,
    isSaving,
    saveError,
    updateShowPricing,
    updateTemplateType,
    updateTemplateName,
    saveChanges,
    discardChanges
  };
};
